import { BaseGridRenderer, RenderConfig } from './BaseGridRenderer';
import { BezierGrid } from './BezierGrid';
import { Cell, CellType, CellState } from './Cell';
import { Point2D, pointInQuad, evaluateCubicBezier } from './BezierMath';

export class BezierGridRenderer extends BaseGridRenderer {
  protected grid: BezierGrid;
  private showControlPoints: boolean = false;
  private showPatchBorders: boolean = true;

  constructor(grid: BezierGrid, config: RenderConfig) {
    super(grid, config);
    this.grid = grid;
    this.resizeCanvas();
  }

  setShowControlPoints(show: boolean): void {
    this.showControlPoints = show;
    this.render();
  }

  setShowPatchBorders(show: boolean): void {
    this.showPatchBorders = show;
    this.render();
  }

  resizeCanvas(): void {
    const width = this.grid.canvasWidth;
    const height = this.grid.canvasHeight;

    this.canvas.width = width * this.dpr;
    this.canvas.height = height * this.dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  render(hoverCell?: Cell | null, previewType?: 'wall' | 'erase' | 'start' | 'goal' | null): void {
    this.clear();
    this.renderCells();
    this.renderGridLines();

    if (this.showControlPoints) {
      this.renderControlPointOverlay();
    }

    if (hoverCell && previewType) {
      this.renderCellPreview(hoverCell, previewType);
      this.renderHoverHighlight(hoverCell);
    }
  }

  cellFromPoint(x: number, y: number): { row: number; col: number } | null {
    for (let row = 0; row < this.grid.rows; row++) {
      for (let col = 0; col < this.grid.cols; col++) {
        if (!this.grid.isCellActive(row, col)) continue;
        const [a, b, c, d] = this.grid.getQuadCorners(row, col);
        if (pointInQuad(x, y, a.x, a.y, b.x, b.y, c.x, c.y, d.x, d.y)) {
          return { row, col };
        }
      }
    }
    return null;
  }

  private renderCells(): void {
    for (let row = 0; row < this.grid.rows; row++) {
      for (let col = 0; col < this.grid.cols; col++) {
        if (!this.grid.isCellActive(row, col)) continue;
        const cell = this.grid.getCellAt(row, col);
        this.renderCell(cell);
      }
    }
  }

  private renderCell(cell: Cell): void {
    const [a, b, c, d] = this.grid.getQuadCorners(cell.row, cell.col);
    const color = this.getCellColor(cell);

    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.moveTo(a.x, a.y);
    this.ctx.lineTo(b.x, b.y);
    this.ctx.lineTo(c.x, c.y);
    this.ctx.lineTo(d.x, d.y);
    this.ctx.closePath();
    this.ctx.fill();

    if (cell.type === CellType.START || cell.type === CellType.GOAL) {
      this.renderMarker(cell);
    }
  }

  private getCellColor(cell: Cell): string {
    switch (cell.type) {
      case CellType.WALL:
        return this.colors.wall;
      case CellType.START:
        return this.colors.start;
      case CellType.GOAL:
        return this.colors.goal;
      default:
        switch (cell.state) {
          case CellState.OPEN:
            return this.colors.open;
          case CellState.CLOSED:
            return this.colors.closed;
          case CellState.PATH:
            return this.colors.path;
          default:
            return this.colors.empty;
        }
    }
  }

  private renderMarker(cell: Cell): void {
    const center = this.grid.getCellCenter(cell.row, cell.col);
    const [a, b] = this.grid.getQuadCorners(cell.row, cell.col);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const edgeLen = Math.sqrt(dx * dx + dy * dy);
    const radius = Math.max(3, edgeLen * 0.15);

    this.ctx.fillStyle = '#ffffff';
    this.ctx.beginPath();
    this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private renderGridLines(): void {
    if (!this.showGrid) return;

    const vertices = this.grid.getVertices();
    const patchRows = this.grid.patchRows;
    const patchCols = this.grid.patchCols;

    // Regular cell grid lines
    this.ctx.strokeStyle = this.colors.grid;
    this.ctx.lineWidth = 1;

    for (let row = 0; row <= this.grid.rows; row++) {
      if (row % patchRows === 0) continue;
      this.ctx.beginPath();
      let drawing = false;
      for (let col = 0; col <= this.grid.cols; col++) {
        const cellRow = Math.min(row, this.grid.rows - 1);
        const cellCol = Math.min(col, this.grid.cols - 1);
        if (!this.grid.isCellActive(cellRow, cellCol)) {
          if (drawing) this.ctx.stroke();
          drawing = false;
          continue;
        }
        if (!drawing) {
          this.ctx.beginPath();
          this.ctx.moveTo(vertices[row][col].x, vertices[row][col].y);
          drawing = true;
        } else {
          this.ctx.lineTo(vertices[row][col].x, vertices[row][col].y);
        }
      }
      if (drawing) this.ctx.stroke();
    }

    for (let col = 0; col <= this.grid.cols; col++) {
      if (col % patchCols === 0) continue;
      this.ctx.beginPath();
      let drawing = false;
      for (let row = 0; row <= this.grid.rows; row++) {
        const cellRow = Math.min(row, this.grid.rows - 1);
        const cellCol = Math.min(col, this.grid.cols - 1);
        if (!this.grid.isCellActive(cellRow, cellCol)) {
          if (drawing) this.ctx.stroke();
          drawing = false;
          continue;
        }
        if (!drawing) {
          this.ctx.beginPath();
          this.ctx.moveTo(vertices[row][col].x, vertices[row][col].y);
          drawing = true;
        } else {
          this.ctx.lineTo(vertices[row][col].x, vertices[row][col].y);
        }
      }
      if (drawing) this.ctx.stroke();
    }

    // Patch boundary lines
    if (!this.showPatchBorders) return;

    this.ctx.strokeStyle = '#d0d5d8';
    this.ctx.lineWidth = 1.6;

    for (let row = 0; row <= this.grid.rows; row += patchRows) {
      this.ctx.beginPath();
      let drawing = false;
      for (let col = 0; col <= this.grid.cols; col++) {
        const adjCol = Math.min(col, this.grid.cols - 1);
        const aboveActive = row > 0 && this.grid.isCellActive(row - 1, adjCol);
        const belowActive = row < this.grid.rows && this.grid.isCellActive(row, adjCol);
        if (!aboveActive && !belowActive) {
          if (drawing) this.ctx.stroke();
          drawing = false;
          continue;
        }
        if (!drawing) {
          this.ctx.beginPath();
          this.ctx.moveTo(vertices[row][col].x, vertices[row][col].y);
          drawing = true;
        } else {
          this.ctx.lineTo(vertices[row][col].x, vertices[row][col].y);
        }
      }
      if (drawing) this.ctx.stroke();
    }

    for (let col = 0; col <= this.grid.cols; col += patchCols) {
      this.ctx.beginPath();
      let drawing = false;
      for (let row = 0; row <= this.grid.rows; row++) {
        const adjRow = Math.min(row, this.grid.rows - 1);
        const leftActive = col > 0 && this.grid.isCellActive(adjRow, col - 1);
        const rightActive = col < this.grid.cols && this.grid.isCellActive(adjRow, col);
        if (!leftActive && !rightActive) {
          if (drawing) this.ctx.stroke();
          drawing = false;
          continue;
        }
        if (!drawing) {
          this.ctx.beginPath();
          this.ctx.moveTo(vertices[row][col].x, vertices[row][col].y);
          drawing = true;
        } else {
          this.ctx.lineTo(vertices[row][col].x, vertices[row][col].y);
        }
      }
      if (drawing) this.ctx.stroke();
    }
  }

  private renderControlPointOverlay(): void {
    const corners = this.grid.getCorners();
    const hCurves = this.grid.getHCurves();
    const vCurves = this.grid.getVCurves();

    // Handle lines (dashed)
    this.ctx.strokeStyle = 'rgba(100, 100, 100, 0.4)';
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([4, 4]);

    for (let r = 0; r < hCurves.length; r++) {
      for (let c = 0; c < hCurves[r].length; c++) {
        const left = corners[r][c];
        const right = corners[r][c + 1];
        const curve = hCurves[r][c];
        this.drawLine(left, curve.cp1);
        this.drawLine(right, curve.cp2);
      }
    }

    for (let r = 0; r < vCurves.length; r++) {
      for (let c = 0; c < vCurves[r].length; c++) {
        const top = corners[r][c];
        const bottom = corners[r + 1][c];
        const curve = vCurves[r][c];
        this.drawLine(top, curve.cp1);
        this.drawLine(bottom, curve.cp2);
      }
    }

    this.ctx.setLineDash([]);

    // Boundary curves
    this.ctx.strokeStyle = 'rgba(80, 80, 80, 0.3)';
    this.ctx.lineWidth = 2;

    for (let r = 0; r < hCurves.length; r++) {
      for (let c = 0; c < hCurves[r].length; c++) {
        this.drawBezierCurve(
          corners[r][c], hCurves[r][c].cp1, hCurves[r][c].cp2, corners[r][c + 1]
        );
      }
    }

    for (let r = 0; r < vCurves.length; r++) {
      for (let c = 0; c < vCurves[r].length; c++) {
        this.drawBezierCurve(
          corners[r][c], vCurves[r][c].cp1, vCurves[r][c].cp2, corners[r + 1][c]
        );
      }
    }

    // Control point circles
    const allPoints = this.grid.getAllDraggablePoints();
    for (const { point, isCorner } of allPoints) {
      this.ctx.beginPath();
      this.ctx.arc(point.x, point.y, isCorner ? 6 : 5, 0, Math.PI * 2);
      this.ctx.fillStyle = isCorner ? '#e74c3c' : '#3498db';
      this.ctx.fill();
      this.ctx.strokeStyle = '#ffffff';
      this.ctx.lineWidth = 2;
      this.ctx.stroke();
    }
  }

  private drawLine(from: Point2D, to: Point2D): void {
    this.ctx.beginPath();
    this.ctx.moveTo(from.x, from.y);
    this.ctx.lineTo(to.x, to.y);
    this.ctx.stroke();
  }

  private drawBezierCurve(p0: Point2D, p1: Point2D, p2: Point2D, p3: Point2D): void {
    this.ctx.beginPath();
    this.ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i <= 40; i++) {
      const t = i / 40;
      const pt = evaluateCubicBezier(t, p0, p1, p2, p3);
      this.ctx.lineTo(pt.x, pt.y);
    }
    this.ctx.stroke();
  }

  private renderCellPreview(cell: Cell, previewType: 'wall' | 'erase' | 'start' | 'goal'): void {
    const [a, b, c, d] = this.grid.getQuadCorners(cell.row, cell.col);

    this.ctx.globalAlpha = 0.5;
    let shouldRender = false;
    let fillColor = '';

    switch (previewType) {
      case 'wall':
        if (cell.type !== CellType.START && cell.type !== CellType.GOAL) {
          shouldRender = true;
          fillColor = this.colors.wall;
        }
        break;
      case 'erase':
        if (cell.type === CellType.WALL) {
          shouldRender = true;
          fillColor = this.colors.empty;
        }
        break;
      case 'start':
        shouldRender = true;
        fillColor = this.colors.start;
        break;
      case 'goal':
        shouldRender = true;
        fillColor = this.colors.goal;
        break;
    }

    if (shouldRender) {
      this.ctx.fillStyle = fillColor;
      this.ctx.beginPath();
      this.ctx.moveTo(a.x, a.y);
      this.ctx.lineTo(b.x, b.y);
      this.ctx.lineTo(c.x, c.y);
      this.ctx.lineTo(d.x, d.y);
      this.ctx.closePath();
      this.ctx.fill();

      if (previewType === 'start' || previewType === 'goal') {
        const center = this.grid.getCellCenter(cell.row, cell.col);
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const edgeLen = Math.sqrt(dx * dx + dy * dy);
        const radius = Math.max(3, edgeLen * 0.15);

        this.ctx.globalAlpha = 1.0;
        this.ctx.fillStyle = '#ffffff';
        this.ctx.beginPath();
        this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.globalAlpha = 0.5;
      }
    }

    this.ctx.globalAlpha = 1.0;
  }

  private renderHoverHighlight(cell: Cell): void {
    const [a, b, c, d] = this.grid.getQuadCorners(cell.row, cell.col);

    this.ctx.strokeStyle = '#ffd700';
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(a.x, a.y);
    this.ctx.lineTo(b.x, b.y);
    this.ctx.lineTo(c.x, c.y);
    this.ctx.lineTo(d.x, d.y);
    this.ctx.closePath();
    this.ctx.stroke();
  }

  protected getCellCenterX(cell: Cell): number {
    return this.grid.getCellCenter(cell.row, cell.col).x;
  }

  protected getCellCenterY(cell: Cell): number {
    return this.grid.getCellCenter(cell.row, cell.col).y;
  }
}
